import { useEffect, useRef, useState } from "react"

import { Flex, Typography } from "antd"
import { useTranslation } from "react-i18next"

import ButtonComponent from "../../../../Components/Button"
import InputComponent from "../../../../Components/Input"
import Icon from "../../../../Icon"

import styles from "./UiNameCell.module.less"

const { Text } = Typography

/**
 * The UI name cell, which is also the only entry point into editing it.
 *
 * Clicking the cell starts edit mode — the row Action menu deliberately carries no rename item. Only
 * one row is ever in edit mode, which the page enforces by owning the id of the row being edited.
 */
const UiNameCell = ({ record, isEditing, isChecking, onStartEdit, onCancel, onSave }) => {
  const { t } = useTranslation()
  const [value, setValue] = useState(record.ui_name || "")
  const inputRef = useRef(null)

  useEffect(() => {
    if (!isEditing) return

    setValue(record.ui_name || "")
    inputRef.current?.focus()
  }, [isEditing, record.ui_name])

  if (!isEditing) {
    return (
      <button
        type="button"
        className={styles.trigger}
        onClick={() => onStartEdit(record)}
        data-testid="payment-matching-ui-name-cell"
      >
        {record.ui_name ? (
          <Text>{record.ui_name}</Text>
        ) : (
          // The raw value stands in for a missing name — the same string the merchant sees today.
          <Text type="secondary" className={styles.placeholderValue}>
            {record.current_name}
          </Text>
        )}
        <Icon type="edit_small" className={styles.affordance} />
      </button>
    )
  }

  const stored = record.ui_name || ""

  return (
    <Flex align="center" gap={8} className={styles.editor}>
      <InputComponent
        ref={inputRef}
        value={value}
        // The raw value as placeholder, so an empty field still shows what the merchant sees.
        placeholder={record.current_name}
        onChange={(event) => setValue(event.target.value)}
        onPressEnter={() => value !== stored && onSave(record, value)}
      />
      <ButtonComponent
        type="primary"
        size="small"
        // Nothing to confirm until the text actually differs from what is stored. While the group is
        // being looked up, Save reports that rather than looking idle.
        disabled={value === stored}
        loading={isChecking}
        onClick={() => onSave(record, value)}
      >
        {t("Save")}
      </ButtonComponent>
      <ButtonComponent size="small" onClick={onCancel}>
        {t("Cancel")}
      </ButtonComponent>
    </Flex>
  )
}

export default UiNameCell
